import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { CaretLeft, BookOpen, CalendarBlank, User, Timer } from "@phosphor-icons/react";

const DISCLAIMER = "Konten ini bersifat edukasi dan tidak menggantikan pemeriksaan, diagnosis, atau konsultasi langsung dengan tenaga kesehatan.";
const fmt = (s) => (s ? new Date(s).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }) : "");
const coverUrl = (a) => (a.cover_file_id ? `${API}/public/articles/${a.id}/cover` : null);

function Cover({ a, className }) {
  const url = coverUrl(a);
  if (url) return <img src={url} alt={a.title} className={className} loading="lazy" />;
  return (
    <div className={`flex items-center justify-center bg-gradient-to-br from-[#EEE6FF] to-[#FDE7F1] ${className}`} data-testid="article-cover-fallback">
      <BookOpen size={40} weight="duotone" className="text-[#7C4DE0]" />
    </div>
  );
}

export default function PublicArticles() {
  const { slug } = useParams();
  return slug ? <Detail slug={slug} /> : <List />;
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#FAF7FF]">
      <header className="border-b border-rose-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <Link to="/booking" data-testid="articles-home" className="flex items-center gap-1.5 text-sm font-bold text-[#7C4DE0]"><CaretLeft size={16} weight="bold" /> Beranda Reservasi</Link>
          <span className="font-heading text-lg font-extrabold text-slate-800">Artikel &amp; Edukasi</span>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-4 py-6">{children}</main>
    </div>
  );
}

function List() {
  const [data, setData] = useState({ items: [], categories: [], total: 0 });
  const [cat, setCat] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/articles`, { params: { category: cat || undefined, page, limit: 12 } })
      .then((r) => setData(r.data)).catch(() => setData({ items: [], categories: [], total: 0 })).finally(() => setLoading(false));
  }, [cat, page]);
  const pages = Math.max(1, Math.ceil((data.total || 0) / 12));
  return (
    <Shell>
      <p className="mb-4 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="articles-disclaimer">{DISCLAIMER}</p>
      {data.categories?.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="articles-categories">
          <button onClick={() => { setCat(""); setPage(1); }} className={`rounded-full px-3 py-1 text-xs font-bold ${!cat ? "bg-[#7C4DE0] text-white" : "bg-white text-slate-600 border border-slate-200"}`}>Semua</button>
          {data.categories.map((c) => (
            <button key={c} onClick={() => { setCat(c); setPage(1); }} className={`rounded-full px-3 py-1 text-xs font-bold ${cat === c ? "bg-[#7C4DE0] text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{c}</button>
          ))}
        </div>
      )}
      {loading ? <p className="py-10 text-center text-slate-400">Memuat…</p> : data.items.length === 0 ? (
        <p className="py-10 text-center text-slate-400" data-testid="articles-empty">Belum ada artikel.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="articles-list">
          {data.items.map((a) => (
            <Link key={a.id} to={`/artikel/${a.slug}`} data-testid={`article-card-${a.slug}`} className="group overflow-hidden rounded-2xl border border-rose-100 bg-white transition-shadow hover:shadow-md">
              <Cover a={a} className="h-40 w-full object-cover" />
              <div className="p-4">
                {a.category && <span className="mb-1 inline-block rounded-full bg-[#EEE6FF] px-2 py-0.5 text-[11px] font-bold text-[#7C4DE0]">{a.category}</span>}
                <h3 className="font-heading text-base font-extrabold text-slate-800 group-hover:text-[#7C4DE0]">{a.title}</h3>
                {a.summary && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{a.summary}</p>}
                <p className="mt-2 text-xs text-slate-400">{fmt(a.published_at)}{a.author ? ` · ${a.author}` : ""}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
      {pages > 1 && (
        <div className="mt-6 flex justify-center gap-2" data-testid="articles-pagination">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg bg-white px-3 py-1.5 text-sm font-bold text-slate-600 disabled:opacity-40">Sebelumnya</button>
          <span className="px-2 py-1.5 text-sm text-slate-500">Hal {page}/{pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="rounded-lg bg-white px-3 py-1.5 text-sm font-bold text-slate-600 disabled:opacity-40">Berikutnya</button>
        </div>
      )}
    </Shell>
  );
}

function Detail({ slug }) {
  const [a, setA] = useState(undefined);
  useEffect(() => {
    axios.get(`${API}/public/articles/${slug}`).then((r) => setA(r.data)).catch(() => setA(null));
  }, [slug]);

  // SEO metadata (no dependency): set title/description/canonical, restore on unmount/change.
  useEffect(() => {
    if (!a || !a.slug) return;
    const prevTitle = document.title;
    const descEl = document.querySelector('meta[name="description"]');
    const prevDesc = descEl ? descEl.getAttribute("content") : null;
    let canonEl = document.querySelector('link[rel="canonical"]');
    const createdCanon = !canonEl;
    const prevCanon = canonEl ? canonEl.getAttribute("href") : null;

    document.title = a.seo_title || a.title || prevTitle;
    if (descEl) descEl.setAttribute("content", a.seo_description || a.summary || prevDesc || "");
    if (!canonEl) { canonEl = document.createElement("link"); canonEl.setAttribute("rel", "canonical"); document.head.appendChild(canonEl); }
    canonEl.setAttribute("href", `${window.location.origin}/artikel/${a.slug}`);

    return () => {
      document.title = prevTitle;
      if (descEl) { if (prevDesc !== null) descEl.setAttribute("content", prevDesc); }
      if (canonEl) { if (createdCanon) canonEl.remove(); else if (prevCanon !== null) canonEl.setAttribute("href", prevCanon); }
    };
  }, [a]);

  if (a === undefined) return <Shell><p className="py-10 text-center text-slate-400">Memuat…</p></Shell>;
  if (a === null) return <Shell><p className="py-10 text-center text-slate-400" data-testid="article-not-found">Artikel tidak ditemukan.</p></Shell>;
  return (
    <Shell>
      <article data-testid="article-detail">
        <Link to="/artikel" className="mb-3 inline-flex items-center gap-1 text-sm font-bold text-[#7C4DE0]"><CaretLeft size={16} /> Semua Artikel</Link>
        {a.category && <span className="mb-2 inline-block rounded-full bg-[#EEE6FF] px-2.5 py-0.5 text-xs font-bold text-[#7C4DE0]">{a.category}</span>}
        <h1 className="font-heading text-2xl font-extrabold leading-tight text-slate-800 sm:text-3xl">{a.title}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-400">
          {a.author && <span className="inline-flex items-center gap-1"><User size={13} /> {a.author}</span>}
          {a.published_at && <span className="inline-flex items-center gap-1"><CalendarBlank size={13} /> {fmt(a.published_at)}</span>}
        </div>
        <Cover a={a} className="mt-4 h-52 w-full rounded-2xl object-cover sm:h-72" />
        {a.summary && <p className="mt-4 text-base font-medium text-slate-600">{a.summary}</p>}
        {/(kehamilan|persalinan)/i.test(a.category || "") && (
          <Link
            to="/timer-kontraksi"
            data-testid="article-timer-cta"
            className="mt-4 flex items-center justify-center gap-2 rounded-2xl border border-[#7C4DE0]/30 bg-gradient-to-r from-[#F5F0FF] to-[#FDE7F1] px-5 py-3 font-heading text-sm font-extrabold text-[#7C4DE0] transition-transform hover:-translate-y-0.5"
          >
            <Timer size={20} weight="duotone" /> Mulai Hitung Kontraksi
          </Link>
        )}
        <div className="mt-4 whitespace-pre-wrap break-words text-[15px] leading-relaxed text-slate-700" data-testid="article-body">{a.body}</div>
        <p className="mt-8 rounded-xl bg-amber-50 px-4 py-3 text-xs text-amber-700" data-testid="article-disclaimer">{DISCLAIMER}</p>
      </article>
    </Shell>
  );
}
